/**
 * Лента склейки карточки товара: своя настройка в браузере, порядок чисел под
 * фото и подписи окна.
 */

import { isValidDate, lastDays, windowDays } from "@/lib/dates";
import { plural } from "@/lib/plans";
import {
  PROFIT_UNIT,
  ROW_LABEL,
  TILE_LABEL,
  type TileMetric,
  type TileRowKey,
  isTileMetric,
} from "@/lib/productNav";
import { buyoutHint, tileText } from "@/lib/similar/tile";
import type { SimilarItem, StripSettings } from "@/lib/similar/types";

const STRIP_KEY = "supply.similar-strip";
const STRIP_DAYS = 60;

/** Дефолт — ЗАКАЗЫ И ВЫКУП %, ровно то, что попросил владелец: лента отвечает
 *  на просьбу без единого клика по шестерёнке. Пустой набор (как у плитки
 *  «Товаров», где всегда виден показатель сортировки) оставил бы ленту совсем
 *  без чисел — то есть беднее, чем она была до этой правки. Окно — 60 дней, то
 *  же, что у продаж карточки и у кольца: третьего дефолтного окна на экране не
 *  заводим. */
export function defaultStripSettings(): StripSettings {
  const [from, to] = lastDays(STRIP_DAYS);
  return { from, to, show: ["orders", "buyout"] };
}

/** Настройка из хранилища, ПОЛЕ ЗА ПОЛЕМ: чужая запись (старая версия вкладки,
 *  ручная правка) иначе отправляла бы в ручку показатель, которого нет, и лента
 *  держала бы 422 на каждой карточке — чинить хранилище некому. */
export function loadStripSettings(): StripSettings {
  const запас = defaultStripSettings();
  try {
    const raw = localStorage.getItem(STRIP_KEY);
    if (!raw) return запас;
    const saved = JSON.parse(raw) as Partial<StripSettings>;
    return {
      from: typeof saved.from === "string" && isValidDate(saved.from) ? saved.from : запас.from,
      to: typeof saved.to === "string" && isValidDate(saved.to) ? saved.to : запас.to,
      show: Array.isArray(saved.show) ? saved.show.filter(isTileMetric) : запас.show,
    };
  } catch {
    return запас;
  }
}

export function saveStripSettings(s: StripSettings): void {
  try {
    localStorage.setItem(STRIP_KEY, JSON.stringify(s));
  } catch {
    // Приватное окно и запрещённое хранилище — не повод ломать ленту.
  }
}

/** Подпись окна ленты под её шестерёнку: «13.07 – 11.09, 60 дней». Окно
 *  заморожено в браузере и через неделю станет старым молча — подпись
 *  единственное, что об этом скажет. */
export function stripWindowLabel(s: StripSettings): string {
  const дней = windowDays(s.from, s.to);
  return `${stripPeriodLabel(s)} · ${дней} ${plural(дней, ["день", "дня", "дней"])}`;
}

/** Короткая подпись периода НАД ЛЕНТОЙ — «13.07 – 11.09», без длины окна
 *  (16.09.26, замечание владельца: «выведи сразу, за какой период эти цифры
 *  посчитаны»). Длина не печатается намеренно: сезон в 92 дня подписью
 *  «92 дня» не называется, а даты называют его точно. */
export function stripPeriodLabel(s: StripSettings): string {
  const день = (iso: string) => iso.slice(8, 10) + "." + iso.slice(5, 7);
  return `${день(s.from)} – ${день(s.to)}`;
}

/**
 * Показатели ленты в КАНОНИЧЕСКОМ порядке списка (`TILE_LABEL`), а не в порядке
 * отметки в шестерёнке: ленты обязаны читаться одинаково, иначе второе число
 * сверху значило бы у двух карточек разное. То же правило, что у `tileRows`
 * плитки; показателя сортировки у ленты нет — она ничего не упорядочивает по
 * выбору человека, и объяснять её порядок числом незачем.
 */
export function stripKeys(show: TileMetric[]): TileRowKey[] {
  const out: TileRowKey[] = [];
  for (const key of Object.keys(TILE_LABEL) as TileMetric[]) {
    if (!show.includes(key)) continue;
    out.push(key);
    // Штука прибыли своей отметки не имеет и встаёт сразу за суммой: под фото
    // они читаются парой, а порознь — как два несвязанных числа.
    if (key === "profit") out.push(PROFIT_UNIT);
  }
  return out;
}

/**
 * Подсказка миниатюры ленты: цвет, ВЫБРАННЫЕ ПОКАЗАТЕЛИ С ПОДПИСЯМИ, название,
 * номер, имя чужого поставщика.
 *
 * Подписи живут здесь, а не под фото: миниатюра 60 px, и «заказы 1 986» в неё
 * не встанет ни при каком шрифте. На экране — только число, а что это за
 * число, отвечает подсказка. Цвет тоже переехал сюда (16.09.26: «цвет
 * бесполезен» — кружок с миниатюры ушёл, как раньше с плитки склейки).
 */
export function stripHint(item: SimilarItem, show: TileMetric[], supplier: string | null): string {
  // Выкуп % называет себя СО СЛАГАЕМЫМИ и одной строкой: отдельная строка
  // подсказки повторила бы тот же процент второй раз, ничего не добавив.
  const числа = stripKeys(show).map(
    (key) =>
      (key === "buyout" ? buyoutHint(item.values) : null) ??
      `${ROW_LABEL[key]} ${tileText(key, item.values?.[key])}`,
  );
  return [
    item.color ?? "цвет не задан",
    ...числа,
    item.name,
    `товар ${item.product_id}`,
    supplier ? `поставщик ${supplier}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}
